import React, { useMemo } from 'react';
import { Typography, Grid2 } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpensePayDate from './ExpensePayDate';
import ExpenseTransactionDate from './ExpenseTransactionDate';
import ExpenseDateRange from './ExpenseDateRange';
import ExpenseCategoryType from './ExpenseCategoryType';
import ExpenseBillType from './ExpenseBillType';
import ExpenseAmount from './ExpenseAmount';
import ExpenseReimbursedAmount from './ExpenseReimbursedAmount';
import { valueAsMileage } from '../../Utils/ValueAsUtils';
import MiscExpenseDescription from './MiscExpenseDescription';
import ExpenseDescription from './ExpenseDescription';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';
import { ExpenseTypeEnum } from './ExpenseType.enum';
import CopyIconButtonWithTooltip from '../CopyIconButtonWithTooltip';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const ClinicianExpenseDetailsForm = (props) => {
    const {
        classes,
        index,
        expense,
        expenseCategories,
        originalExpenseEntries,
        isViewOnly,
        isInvoiceReviewLocked = false,
        mode,
        enableEditByCorrection = false,
    } = props;
    const { control, watch, errors, setValue } = useFormContext();
    const currentExpense = watch(`expenseEntries[${index}]`);
    const expenseCategory = currentExpense?.category;
    const expenseType = currentExpense?.type;
    const isExpenseTravel = expenseCategory && expenseCategory === ExpenseCategoryEnum.Travel;
    const isMiscExpense =
        expenseCategory === ExpenseCategoryEnum.Misc && expenseType === ExpenseTypeEnum.Misc;
    const canRemoveTravelDateFields =
        expenseType === ExpenseTypeEnum.BaggageFee ||
        expenseType === ExpenseTypeEnum.Fuel ||
        expenseType === '';
    const isClinicianReimbursement = true;
    const hasMileage =
        expenseType &&
        expenseCategory &&
        expenseCategory === ExpenseCategoryEnum.Travel &&
        expenseType === ExpenseTypeEnum.Mileage;
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === 'Posted' ||
            (currentStatus === 'OnHoldReimbursement' && originalStatus === 'Posted'),
        [originalStatus, currentStatus]
    );
    const isApprovedOrInvoiced = [
        ExpenseStatusValuesEnum.Approved,
        ExpenseStatusValuesEnum.Invoiced,
    ].includes(originalStatus);
    const isPendingSubmission = expense.status === 'PendingSubmission';
    const expenseTypes = useMemo(() => {
        if (!expenseCategory) return [];
        if (!expenseCategories) return [];
        const { types } = expenseCategories.find((option) => option.category === expenseCategory);
        return types || [];
    }, [expenseCategory, expenseCategories]);
    const originalExpense = (function () {
        // If my tab original db expense is posted disable
        if (
            expense.id &&
            expense.id > 0 &&
            originalExpenseEntries &&
            originalExpenseEntries.length > 0
        ) {
            return originalExpenseEntries.find((entry) => entry.id === expense.id);
        }
        return null;
    })();
    const isAnyExpensePosted = (function () {
        // Check if anything is Posted
        if (originalExpenseEntries && originalExpenseEntries.length > 0) {
            return originalExpenseEntries.some((entry) => entry.status === 'Posted');
        }
        return false;
    })();

    const { ltoeWebExpensesEnableExpenseDetailDescription } = useFlags();

    const isParentExpenseEntry = index === 0;
    const isFieldLocked = isViewOnly || isInvoiceReviewLocked;
    const shouldShowCopySubmittedAmount = isParentExpenseEntry && !isFieldLocked;
    const expenseAmountValue = watch(`expenseEntries[${index}].amount`);
    const copyExpenseAmountIntoReimbursedAmount = () => {
        if (expenseAmountValue) {
            setValue(`expenseEntries[${index}].reimbursedAmount`, expenseAmountValue, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Expense Details
            </Typography>
            <Grid2 container spacing={3}>
                {Boolean(expense.id) && (
                    <Grid2 item size={{ xs: 12, md: 6 }}>
                        <ExpenseTextField
                            id="expense-id"
                            label="Expense ID"
                            value={String(expense.id)}
                            disabled
                        />
                    </Grid2>
                )}
                <Grid2 item size={{ xs: 12, md: 6 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].merchantName`}
                        defaultValue={expense.merchantName}
                        isViewOnly={isViewOnly}
                        rules={{
                            required:
                                expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                                expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                !hasMileage
                                    ? 'Merchant name is required'
                                    : false,
                        }}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                id="merchant-name"
                                onChange={onChange}
                                inputProps={{ maxLength: 50 }}
                                value={value}
                                placeholder="Merchant name"
                                label="Merchant name"
                                required={
                                    expense?.status !== ExpenseStatusValuesEnum.Abandoned &&
                                    !hasMileage
                                }
                                disabled={
                                    (isPosted && isFieldLocked) ||
                                    (isPendingSubmission && !enableEditByCorrection) ||
                                    isFieldLocked
                                }
                                error={Boolean(errors.expenseEntries?.[index]?.merchantName)}
                                helperText={errors.expenseEntries?.[index]?.merchantName?.message}
                            />
                        )}
                    />
                </Grid2>

                <ExpenseBillType
                    index={index}
                    expense={expense}
                    isClinicianReimbursement={isClinicianReimbursement}
                    isSGA={[]}
                    isPosted={isPosted}
                    isPendingSubmission={isPendingSubmission}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                />
                <ExpensePayDate
                    index={index}
                    expense={expense}
                    mode="reimbursement"
                    isPosted={isPosted}
                    isApprovedOrInvoiced={isApprovedOrInvoiced}
                    isPendingSubmission={isPendingSubmission}
                    isViewOnly={isViewOnly}
                    enableEditByCorrection={enableEditByCorrection}
                />

                <ExpenseTransactionDate
                    index={index}
                    expense={expense}
                    isReimbursement
                    mode={mode}
                    isPosted={isPosted}
                    isPendingSubmission={isPendingSubmission}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                />

                <ExpenseCategoryType
                    expenseTypes={expenseTypes}
                    index={index}
                    expense={expense}
                    expenseCategories={expenseCategories}
                    isSGA={[]}
                    isPosted={isPosted}
                    isPendingSubmission={isPendingSubmission}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                    mode={mode}
                    enableEditByCorrection={enableEditByCorrection}
                />
                {isMiscExpense && (
                    <MiscExpenseDescription
                        index={index}
                        expense={expense}
                        isMiscExpense={isMiscExpense}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                )}
                {isExpenseTravel && !canRemoveTravelDateFields && (
                    <ExpenseDateRange
                        index={index}
                        expense={currentExpense}
                        mode={mode}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                        enableEditByCorrection={enableEditByCorrection}
                    />
                )}

                <ExpenseAmount
                    index={index}
                    expense={expense}
                    originalExpense={originalExpense}
                    originalExpenseEntries={originalExpenseEntries}
                    isReimbursement
                    isPosted={isPosted}
                    isPendingSubmission={isPendingSubmission}
                    isAnyExpensePosted={isAnyExpensePosted}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                    enableEditByCorrection={enableEditByCorrection}
                    updateAllAmounts={() => {}}
                    size={shouldShowCopySubmittedAmount ? 'grow' : undefined}
                />
                {shouldShowCopySubmittedAmount && (
                    <CopyIconButtonWithTooltip
                        title="Copy Submitted Amount"
                        onClick={copyExpenseAmountIntoReimbursedAmount}
                        disabled={!expenseAmountValue}
                    />
                )}
                <ExpenseReimbursedAmount
                    index={index}
                    expense={currentExpense}
                    isPendingSubmission={isPendingSubmission}
                    isPosted={isPosted}
                    isViewOnly={isViewOnly}
                    isInvoiceReviewLocked={isInvoiceReviewLocked}
                    enableEditByCorrection={enableEditByCorrection}
                    size={shouldShowCopySubmittedAmount ? 'grow' : undefined}
                />

                {hasMileage && (
                    <Grid2 item size={{ xs: 12, sm: 6 }}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].mileage`}
                            defaultValue={expense.mileage}
                            rules={{
                                required:
                                    expense?.status !== ExpenseStatusValuesEnum.NonReimbursable &&
                                    expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                        ? 'Miles driven is required'
                                        : false,
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseTextField
                                    id="miles-driven"
                                    onChange={(e) => onChange(valueAsMileage(e.target.value))}
                                    value={value}
                                    placeholder="Enter number of miles"
                                    label="Miles driven"
                                    required={
                                        expense?.status !==
                                            ExpenseStatusValuesEnum.NonReimbursable &&
                                        expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                    }
                                    disabled={
                                        (isPosted && isFieldLocked) ||
                                        (isPendingSubmission && !enableEditByCorrection) ||
                                        isFieldLocked
                                    }
                                    error={Boolean(errors.expenseEntries?.[index]?.mileage)}
                                    helperText={errors.expenseEntries?.[index]?.mileage?.message}
                                />
                            )}
                        />
                    </Grid2>
                )}
                {ltoeWebExpensesEnableExpenseDetailDescription && (
                    <ExpenseDescription
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        isInvoiceReviewLocked={isInvoiceReviewLocked}
                    />
                )}
                {/* <ExpenseComments index={index} expense={expense} isPosted={isPosted} isPendingSubmission={isPendingSubmission} isViewOnly={isViewOnly}/> */}
            </Grid2>
        </>
    );
};

export default withStyles(styles)(ClinicianExpenseDetailsForm);
